import React from "react";
import { Box } from "@mui/material";

const EdgeCameraControls = ({
  edgeHover,
  edgeControlWidth,
  rotateCamera,
}) => {
  const isCompact = edgeControlWidth <= 50;
  const sideInset = isCompact
    ? "var(--edge-side-inset-compact, 40px)"
    : "var(--edge-side-inset, 0px)";
  const topInset = isCompact
    ? "var(--edge-top-inset-compact, 40px)"
    : "var(--edge-top-inset, 0px)";
  const bottomInset = isCompact
    ? "var(--edge-bottom-inset-compact, 46px)"
    : "var(--edge-bottom-inset, 0px)";
  const sideButtonWidth = isCompact
    ? "var(--edge-side-button-width-compact, 50px)"
    : "var(--edge-side-button-width, 80px)";
  const topBottomButtonHeight = isCompact
    ? "var(--edge-top-bottom-button-height-compact, 46px)"
    : "var(--edge-top-bottom-button-height, 80px)";
  const edgeFontSize = isCompact
    ? "var(--edge-font-size-compact, 18px)"
    : "var(--edge-font-size, 20px)";
  const edgeCornerClearSize = isCompact
    ? "var(--edge-corner-clear-size-compact, 24px)"
    : "var(--edge-corner-clear-size, 150px)";
  const edgeCornerFadeSize = isCompact
    ? "var(--edge-corner-fade-size-compact, 40px)"
    : "var(--edge-corner-fade-size, 190px)";
  const sideMaskImage = `linear-gradient(to bottom, transparent 0%, transparent ${edgeCornerClearSize}, black ${edgeCornerFadeSize}, black calc(100% - ${edgeCornerFadeSize}), transparent calc(100% - ${edgeCornerClearSize}), transparent 100%)`;
  const topBottomMaskImage = `linear-gradient(to right, transparent 0%, transparent ${edgeCornerClearSize}, black ${edgeCornerFadeSize}, black calc(100% - ${edgeCornerFadeSize}), transparent calc(100% - ${edgeCornerClearSize}), transparent 100%)`;
  const sideTopEdge = `calc(env(safe-area-inset-top, 0px) + ${topInset})`;
  const sideBottomEdge = `calc(env(safe-area-inset-bottom, 0px) + ${bottomInset})`;
  const horizontalEdgeLeft = `calc(env(safe-area-inset-left, 0px) + ${sideInset})`;
  const horizontalEdgeRight = `calc(env(safe-area-inset-right, 0px) + ${sideInset})`;

  const blockScroll = (event) => {
    event.preventDefault();
  };

  const subtleGlassButtonSx = {
    position: "absolute",
    zIndex: 0,
    border: "var(--edge-glass-border, 1px solid rgba(255, 255, 255, 0.15))",
    borderRadius: "0",
    background:
      "var(--edge-glass-gradient, linear-gradient(160deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.04) 100%))",
    color: "var(--edge-glass-color, rgba(255, 255, 255, 0.8))",
    boxShadow:
      "var(--edge-glass-shadow, 0 8px 20px rgba(0, 0, 0, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.12))",
    backdropFilter:
      "blur(var(--edge-glass-blur, 8px)) saturate(var(--edge-glass-saturate, 108%))",
    WebkitBackdropFilter:
      "blur(var(--edge-glass-blur, 8px)) saturate(var(--edge-glass-saturate, 108%))",
    transition: "opacity 180ms ease, transform 180ms ease, background 180ms ease",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: edgeFontSize,
    lineHeight: 1,
    fontWeight: 700,
    "&:hover": {
      background:
        "var(--edge-glass-gradient-hover, linear-gradient(160deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.06) 100%))",
      color: "var(--edge-glass-color-hover, rgba(255, 255, 255, 0.74))",
    },
    "&:active": {
      background:
        "var(--edge-glass-gradient-active, linear-gradient(160deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.05) 100%))",
    },
    "&:focus-visible": {
      outline: "var(--edge-focus-outline, 2px solid rgba(255, 255, 255, 0.42))",
      outlineOffset: "2px",
    },
  };

  return (
    <Box
      className="edge-controls-overlay"
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 13,
      }}
    >
      <Box
        className="edge-button edge-button-left"
        component="button"
        type="button"
        aria-label="Rotate camera left"
        onClick={(event) => {
          event.stopPropagation();
          rotateCamera("-4,0");
        }}
        onWheel={blockScroll}
        onTouchMove={blockScroll}
        sx={{
          ...subtleGlassButtonSx,
          width: sideButtonWidth,
          left: horizontalEdgeLeft,
          top: sideTopEdge,
          bottom: sideBottomEdge,
          opacity: edgeHover.left ? "var(--edge-glass-opacity, 0.62)" : 0,
          transform: edgeHover.left ? "translateX(0)" : "translateX(-10px)",
          maskImage: sideMaskImage,
          WebkitMaskImage: sideMaskImage,
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          pointerEvents: edgeHover.left ? "auto" : "none",
        }}
      >
        {"◀"}
      </Box>

      <Box
        className="edge-button edge-button-right"
        component="button"
        type="button"
        aria-label="Rotate camera right"
        onClick={(event) => {
          event.stopPropagation();
          rotateCamera("4,0");
        }}
        onWheel={blockScroll}
        onTouchMove={blockScroll}
        sx={{
          ...subtleGlassButtonSx,
          width: sideButtonWidth,
          right: horizontalEdgeRight,
          top: sideTopEdge,
          bottom: sideBottomEdge,
          opacity: edgeHover.right ? "var(--edge-glass-opacity, 0.62)" : 0,
          transform: edgeHover.right ? "translateX(0)" : "translateX(10px)",
          maskImage: sideMaskImage,
          WebkitMaskImage: sideMaskImage,
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          pointerEvents: edgeHover.right ? "auto" : "none",
        }}
      >
        {"▶"}
      </Box>

      <Box
        className="edge-button edge-button-top"
        component="button"
        type="button"
        aria-label="Rotate camera up"
        onClick={(event) => {
          event.stopPropagation();
          rotateCamera("0,2");
        }}
        onWheel={blockScroll}
        onTouchMove={blockScroll}
        sx={{
          ...subtleGlassButtonSx,
          width: "auto",
          height: topBottomButtonHeight,
          left: horizontalEdgeLeft,
          right: horizontalEdgeRight,
          top: `calc(env(safe-area-inset-top, 0px) + ${topInset})`,
          opacity: edgeHover.top ? "var(--edge-glass-opacity, 0.62)" : 0,
          transform: edgeHover.top ? "translateY(0)" : "translateY(-10px)",
          maskImage: topBottomMaskImage,
          WebkitMaskImage: topBottomMaskImage,
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          pointerEvents: edgeHover.top ? "auto" : "none",
        }}
      >
        {"▲"}
      </Box>

      <Box
        className="edge-button edge-button-bottom"
        component="button"
        type="button"
        aria-label="Rotate camera down"
        onClick={(event) => {
          event.stopPropagation();
          rotateCamera("0,-2");
        }}
        onWheel={blockScroll}
        onTouchMove={blockScroll}
        sx={{
          ...subtleGlassButtonSx,
          width: "auto",
          height: topBottomButtonHeight,
          left: horizontalEdgeLeft,
          right: horizontalEdgeRight,
          bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomInset})`,
          opacity: edgeHover.bottom ? "var(--edge-glass-opacity, 0.62)" : 0,
          transform: edgeHover.bottom ? "translateY(0)" : "translateY(10px)",
          maskImage: topBottomMaskImage,
          WebkitMaskImage: topBottomMaskImage,
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          pointerEvents: edgeHover.bottom ? "auto" : "none",
        }}
      >
        {"▼"}
      </Box>
    </Box>
  );
};

export default EdgeCameraControls;
